import React from "react";
import {
  Card,
  Box,
  Typography,
  Button,
  Chip
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import * as MuiIcons from "@mui/icons-material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BuildIcon from '@mui/icons-material/Build';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import { Avatar, Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { groupNameFor, groupIdFor } from "./Utils/neighborhood.utils";


const WorkOrderCard = ({ order, getAssignedUserName, getTypeName, getStatusInfo }) => {
  const theme = useTheme();
  const statusInfo = getStatusInfo(order.status);
  const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];

  const openerId = order?.opened_by_user_id ?? null;
  const openerLabel = (() => {
  if (order?.opened_by_name) return order.opened_by_name;
  if (openerId != null) {
    const name = getAssignedUserName ? getAssignedUserName(openerId) : null;
    return name || `#${openerId}`;
  }
  return null;
})();


  // inside WorkOrderCard, before return
const assignees = (() => {
  // 1) Preferred: full objects returned by API
  if (Array.isArray(order?.assignees_details) && order.assignees_details.length) {
    const valid = order.assignees_details.filter(a => a?.user_id && a.user_id !== 0);
    if (valid.length) {
      return valid.map(a => ({
        id: a.user_id,
        name: `${a.name || ""} ${a.lastname || ""}`.trim(),
        isPrimary: !!a.is_primary,
      }));
    }
  }

  // 2) Fallback: just IDs array, resolve names with helper
  if (Array.isArray(order?.assignees) && order.assignees.length) {
    const valid = order.assignees.filter(id => id && id !== 0);
    if (valid.length) {
      return valid.map((id, idx) => ({
        id,
        name: getAssignedUserName ? (getAssignedUserName(id) || `#${id}`) : `#${id}`,
        isPrimary: idx === 0, // first = primary fallback
      }));
    }
  }

  // 3) Legacy single field
  if (order?.assign_to && order.assign_to !== 0) {
    const id = order.assign_to;
    const name = getAssignedUserName ? (getAssignedUserName(id) || `#${id}`) : `#${id}`;
    return [{ id, name, isPrimary: true }];
  }

  // 4) No valid assignee
  return [];
})();


  const initialsOf = (full) =>
  (full || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || "")
    .join("") || "?";

 
return (
    <Card
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap", // ✅ allow wrapping
        borderLeft: `8px solid ${theme.palette[statusInfo?.color]?.main || statusInfo?.color || "#ccc"}`,
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
        gap: 2,
        alignItems: "flex-start", // ✅ align content to top
      }}
    >
      {/* LEFT COLUMN */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h6">WO-{order.order_id}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon fontSize="small" />
          <Typography variant="body2"><strong>Property ID:</strong> {order.property_id}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon fontSize="small" />
          <Typography variant="body2"><strong>Member Name:</strong> {order.fullname || "N/A"}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon fontSize="small" />
          <Typography variant="body2"><strong>Address:</strong> {order.full_address || "N/A"}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon fontSize="small" />
          <Typography variant="body2"><strong>Membership Type:</strong> {groupNameFor(order?.ClubMembershipTypeCode) || "N/A"} </Typography>
          
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlashOnIcon fontSize="small" />
          <Typography variant="body2"><strong>Priority:</strong> {order.priority}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon fontSize="small" />
          <Typography variant="caption"><strong>Created:</strong> {new Date(order.created_at).toLocaleDateString()}</Typography>
        </Box>

 <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon fontSize="small" />
          {openerLabel ? (
            <Tooltip title={`Opened by ${openerLabel}`}>
              <Chip
                size="small"
                variant="outlined"
                label={openerLabel}
                avatar={
                  <Avatar
                    sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "grey.300" }}
                  >
                    {initialsOf(openerLabel)}
                  </Avatar>
                }
              />
            </Tooltip>
          ) : (
            <Chip size="small" label="Opened by: Unknown" variant="outlined" />
          )}
        </Box>
        <Box sx={{ mt: 1 }}>
          <Button
            component={Link}
            to={`${order.order_id}`}
            variant="outlined"
            size="small"
          >
            View Details
          </Button>
        </Box>
{/* Assigned To (multi, chips) */}
<Box sx={{ mt: 1, display: "flex", alignItems: "flex-start", gap: 1 }}>
  <PersonIcon fontSize="small" sx={{ mt: "2px" }} />
  {assignees.length ? (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
      <Typography variant="body2" sx={{ lineHeight: 1 }}>
        <strong>Assigned To:</strong>
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {assignees.map((a) => {
          const label = a.name || `#${a.id}`;
          return (
            <Tooltip key={a.id} title={a.isPrimary ? `${label} (Primary)` : label}>
              <Chip
                size="small"
                variant="outlined"
                label={label}
                avatar={
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 11,
                      bgcolor: a.isPrimary ? "primary.main" : "grey.300",
                      color: a.isPrimary ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    {initialsOf(label)}
                  </Avatar>
                }
                icon={a.isPrimary ? <StarIcon fontSize="small" /> : undefined}
                sx={{
                  "& .MuiChip-icon": { ml: 0.25 },      // tighten star spacing
                  maxWidth: "100%",
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  ) : (
    <Chip label="Not Assigned" size="small" color="default" />
  )}
</Box>
      </Box>

      

      {/* RIGHT COLUMN */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, overflowWrap: "break-word" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon fontSize="small" />
          <Typography variant="body2"><strong>Type:</strong> {getTypeName(order.workorder_type_id)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WaterDropIcon fontSize="small" />
          <Typography variant="body2"><strong>Subtype:</strong> {order.subtype_name || "N/A"}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RoomPreferencesIcon fontSize="small" />
          <Typography variant="body2"><strong>Location:</strong> {order.location_name || "N/A"}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <DescriptionIcon fontSize="small" />
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "normal", 
              wordBreak: "break-word" 
            }}
          >
            <strong>Description:</strong> {order.description}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          {IconComponent && (
            <IconComponent fontSize="small" color={statusInfo?.color || "action"} />
          )}
          <Chip
            label={statusInfo?.statusname || "Unknown"}
            color={statusInfo?.color || "default"}
          />
        </Box>
      </Box>
    </Card>
  );
};

export default WorkOrderCard;
